import { useEffect } from "react";
import PageHeader from "../components/layout/PageHeader";
import PriceCalculator from "../components/features/PriceCalculator";
import { setPageMeta, JsonLd, servicePriceSchema, breadcrumbSchema, speakableSchema } from "../lib/schema";
import { STATE_RATES, FIXED_PRICE_ITEMS, formatGBP } from "../lib/pricing";

const SERVICE_TIERS = [
  { tier: "£0 – £100", fee: "£20 flat" },
  { tier: "£101 – £300", fee: "15% of product value" },
  { tier: "£301 – £600", fee: "12% of product value" },
  { tier: "£600+", fee: "Min. £75 (negotiated)" },
  { tier: "Complex sourcing (no link)", fee: "+£30 add-on" },
];

const FREIGHT_TIERS = [
  { weight: "Under 2 kg", rate: "£35 flat" },
  { weight: "2 kg – 5 kg", rate: "£50 flat" },
  { weight: "Above 5 kg", rate: "Per-kg rate by state (see table)" },
];

export default function PricingPage() {
  useEffect(() => {
    setPageMeta({
      title: "MPS Pricing & Quote Calculator | My Private Shopper",
      description:
        "Full MPS rate card. Service fees from £20, freight from £35, plus interactive quote calculator. Transparent UK-to-Nigeria personal shopping pricing.",
      canonical: "https://privateshopper.co.uk/pricing",
    });
  }, []);

  return (
    <>
      <JsonLd data={servicePriceSchema} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Pricing" },
      ])} />
      <JsonLd data={speakableSchema(["[data-testid='page-header'] h1", "[data-testid='pricing-service-table']", "[data-testid='pricing-freight-table']"])} />
      <PageHeader
        overline="Pricing"
        title={<>Transparent rates, <span className="italic text-[#C9A96E]">no surprises</span>.</>}
        body="Service fees scale with product value. Freight scales with weight or destination. Try the live calculator below."
      />

      <PriceCalculator />

      <section className="bg-[#F8F5F0] py-20 border-t border-[#E5E0D8]">
        <div className="container-mps grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div data-testid="pricing-service-table">
            <div className="overline text-[#C9A96E] mb-4">Service Fee</div>
            <h2 className="font-serif text-3xl md:text-4xl mb-8">Based on product value</h2>
            <table className="w-full text-left">
              <thead>
                <tr className="overline text-[#6B7280] border-b border-[#E5E0D8]">
                  <th className="py-3 pr-4 font-medium">Product Value</th>
                  <th className="py-3 font-medium">Fee</th>
                </tr>
              </thead>
              <tbody>
                {SERVICE_TIERS.map((r) => (
                  <tr key={r.tier} className="border-b border-[#E5E0D8]">
                    <td className="py-4 pr-4 text-[#1A1A1A]">{r.tier}</td>
                    <td className="py-4 text-[#1A1A1A]">{r.fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div data-testid="pricing-freight-table">
            <div className="overline text-[#C9A96E] mb-4">Freight Rates</div>
            <h2 className="font-serif text-3xl md:text-4xl mb-8">By weight</h2>
            <table className="w-full text-left">
              <thead>
                <tr className="overline text-[#6B7280] border-b border-[#E5E0D8]">
                  <th className="py-3 pr-4 font-medium">Weight</th>
                  <th className="py-3 font-medium">Rate</th>
                </tr>
              </thead>
              <tbody>
                {FREIGHT_TIERS.map((r) => (
                  <tr key={r.weight} className="border-b border-[#E5E0D8]">
                    <td className="py-4 pr-4 text-[#1A1A1A]">{r.weight}</td>
                    <td className="py-4 text-[#1A1A1A]">{r.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F5F0] py-20">
        <div className="container-mps">
          <div className="overline text-[#C9A96E] mb-4">By State (above 5 kg)</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-10">All 36 states + FCT</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-2">
            {Object.entries(STATE_RATES).map(([state, r]) => (
              <div
                key={state}
                className="flex justify-between items-baseline py-3 border-b border-[#E5E0D8]"
                data-testid={`pricing-state-${state.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="text-[#1A1A1A]">{state}</span>
                <span className="text-sm text-[#6B7280] tabular-nums">
                  £{r.perKg.toFixed(2)}/kg · min £{r.min}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-[#F8F5F0] py-24">
        <div className="container-mps">
          <div className="overline text-[#C9A96E] mb-4">Fixed-Price Items</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-10">
            Flat freight to Lagos — regardless of weight
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-2">
            {Object.entries(FIXED_PRICE_ITEMS).map(([item, price]) => (
              <div
                key={item}
                className="flex justify-between items-baseline py-3 border-b border-[#C9A96E]/15"
                data-testid={`pricing-fixed-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="text-[#F8F5F0]/85">{item}</span>
                <span className="font-medium text-[#C9A96E] tabular-nums">{formatGBP(price)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
